import { Config, Driver, driver, State } from 'driver.js'
import { useCallback, useEffect, useState } from 'react'

import { DriverStep } from '../types/DriverStep'

/**
 * The following hook instantiate a new driver.js instance which is used for touring the application.
 *  - The hook accepts an array of steps which are used to guide the user through the tour.
 *  - Use the hook on each page where you want to start a new tour and pass in the steps for that page.
 */

export type UseDriverArgs = {
  initialSteps: DriverStep[]
  initialConfig?: Partial<Config>
}

export type UseDriverReturn = {
  driverInstance: Driver | null
  startTour: () => void
  moveToNextStep: () => void
  getTourState: () => State | undefined
  setSteps: (steps: DriverStep[]) => void
  setConfig: (config: Partial<Config>) => void
}

/**
 * @param steps - An array of steps to guide the user through the tour.
 * @param customConfig - custom configuration for the driver.js instance.
 * @param onTourComplete - A callback function to execute when the tour is completed.
 */

export const useDriver = ({
  initialSteps,
  initialConfig = {
    animate: true,
    showProgress: true,
    allowClose: true,
    stagePadding: 0,
  },
}: UseDriverArgs): UseDriverReturn => {
  const [driverInstance, setDriverInstance] = useState<Driver | null>(null)

  useEffect(() => {
    // Initialize driver once
    setDriverInstance(driver({
      animate: true,
      showProgress: true,
      allowClose: true,
      stagePadding: 0,
      steps: initialSteps,
      ...initialConfig,
    }))
    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setConfig = useCallback((config: Config) => {
    if (!driverInstance) return

    driverInstance.setConfig({
      ...driverInstance.getConfig(),
      ...config,
      onHighlightStarted: (_element, step, _operations) => {
        const customStep = step as DriverStep

        if (customStep.requireInteraction) {
          if (step.popover) {
            step.popover.disableButtons = ['next']

            const originalDescription = step.popover.description

            let instruction = '<strong>Complete the action to continue</strong>'

            if (customStep.interactionType === 'dragDrop') {
              instruction = '<strong>Drag and drop a widget to continue</strong>'
            }
            else if (customStep.interactionType === 'click') {
              instruction = '<strong>Click to continue</strong>'
            }

            step.popover.description = `${originalDescription}<br/><br/>${instruction}`
          }
        }
      },
    })
  }, [driverInstance])

  const setSteps = useCallback((steps: DriverStep[]) => {
    if (!driverInstance) return

    const validSteps = steps.filter(step => {
      if (step.element === undefined) return true

      const elementExists = typeof step.element === 'string'
        ? document.querySelector(step.element)
        : step.element

      if (!elementExists) {
        console.warn(`Tour step element not found: ${step.element}`)
      }

      return !!elementExists
    })

    driverInstance.setSteps(validSteps)
  }, [driverInstance])

  const startTour = useCallback(() => {
    if (!driverInstance) return

    try {
      driverInstance.drive()
    } catch (error) {
      console.error('Error starting tour:', error)
    }
  }, [driverInstance])

  const moveToNextStep = useCallback(() => {
    if (!driverInstance) return

    const state = driverInstance.getState()
    const currentConfig = driverInstance.getConfig()

    if (state && currentConfig && currentConfig.steps && state.activeIndex && state.activeIndex < currentConfig.steps.length - 1) {
      driverInstance.getConfig().disableButtons = []
      driverInstance.moveNext()
    }
  }, [driverInstance])

  const getTourState = useCallback(() => {
    return driverInstance?.getState()
  }, [driverInstance])

  return {
    driverInstance,
    startTour,
    moveToNextStep,
    getTourState,
    setSteps,
    setConfig,
  }
}